import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, RoleBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { COMMON_TIMEZONES } from "@/lib/campaignForm";
import {
  User as UserIcon, LockKey, EnvelopeSimple, GlobeHemisphereWest, CaretUpDown, Check,
} from "@phosphor-icons/react";

function TimezoneCombobox({ value, onChange, timezones, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => {
    const base = Array.isArray(timezones) && timezones.length ? timezones : COMMON_TIMEZONES;
    const list = [...base];
    const cur = (value || "").trim();
    if (cur && !list.includes(cur)) list.unshift(cur);
    return list;
  }, [timezones, value]);
  const display = (value || "").trim() || "Select timezone…";

  return (
    <Popover open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3"
        >
          <span className={`truncate ${!(value || "").trim() ? "text-muted-foreground" : ""}`}>
            {display}
          </span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {zones.map((z) => (
                <CommandItem
                  key={z}
                  value={z}
                  data-testid={`${testid}-option-${z.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                  onSelect={() => { onChange(z); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${(value || "") === z ? "opacity-100" : "opacity-0"}`} />
                  {z}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const impersonated = !!user?._impersonated;
  const isCustomer = user?.role === "customer";

  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [timezone, setTimezone] = useState("UTC");
  const [timezones, setTimezones] = useState(["UTC"]);
  const [savingTimezone, setSavingTimezone] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.id, user?.name]);

  useEffect(() => {
    if (!isCustomer) return undefined;
    let cancelled = false;
    api.get("/customer-settings")
      .then(({ data }) => {
        if (cancelled) return;
        setTimezone(data?.timezone || user?.timezone || "UTC");
        const zones = data?.timezones?.length ? data.timezones : COMMON_TIMEZONES;
        setTimezones(zones);
      })
      .catch(() => {
        if (cancelled) return;
        setTimezone(user?.timezone || "UTC");
        setTimezones(COMMON_TIMEZONES);
      });
    return () => { cancelled = true; };
  }, [isCustomer, user?.tenant_id, user?.timezone]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (impersonated) {
      toast.error("Cannot change profile while using backdoor login.");
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Name is required.");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name: trimmedName });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err) || "Could not update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveTimezone = async (e) => {
    e.preventDefault();
    if (!isCustomer) return;
    if (impersonated) {
      toast.error("Cannot change timezone while using backdoor login.");
      return;
    }
    setSavingTimezone(true);
    try {
      const { data } = await api.put("/customer-settings/general", { timezone });
      setTimezone(data.timezone || timezone);
      await refresh();
      toast.success("Timezone saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err) || "Could not update timezone.");
    } finally {
      setSavingTimezone(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (impersonated) {
      toast.error("Cannot change password while using backdoor login.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await api.post("/auth/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err) || "Could not update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <PageHeader
        title="Personal profile"
        subtitle={isCustomer
          ? "Update your name, account timezone, and password. Email cannot be changed."
          : "Update your name and password for this login. Email cannot be changed."}
      />

      {impersonated && (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
          data-testid="profile-impersonation-notice"
        >
          You are viewing this account via backdoor login
          {user?._impersonator_email ? ` (${user._impersonator_email})` : ""}.
          Profile and password changes are disabled.
        </div>
      )}

      <section
        className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6 space-y-5"
        data-testid="profile-details-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent text-primary grid place-items-center">
              <UserIcon className="w-5 h-5" weight="duotone" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Personal details</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Visible on your account across the control plane.
              </p>
            </div>
          </div>
          <RoleBadge role={user?.role} />
        </div>

        <form onSubmit={saveProfile} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Full name</Label>
            <Input
              id="profile-name"
              data-testid="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={impersonated || savingProfile}
              autoComplete="name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-email">Email</Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="profile-email"
                data-testid="profile-email"
                type="email"
                className="pl-9 bg-muted/50"
                value={user?.email || ""}
                readOnly
                disabled
                autoComplete="email"
                title="Email cannot be changed"
              />
            </div>
            <p className="text-xs text-muted-foreground">Email cannot be changed.</p>
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              className="rounded-lg"
              disabled={impersonated || savingProfile}
              data-testid="profile-save-details"
            >
              {savingProfile ? "Saving…" : "Save details"}
            </Button>
          </div>
        </form>
      </section>

      {isCustomer && (
        <section
          className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6 space-y-5"
          data-testid="profile-timezone-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent text-primary grid place-items-center">
              <GlobeHemisphereWest className="w-5 h-5" weight="duotone" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Timezone</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Used for campaign scheduling, callbacks, and local timestamps. Stored times remain UTC.
              </p>
            </div>
          </div>
          <form onSubmit={saveTimezone} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label>Account timezone</Label>
              <TimezoneCombobox
                value={timezone}
                onChange={setTimezone}
                timezones={timezones}
                disabled={impersonated || savingTimezone}
                testid="profile-timezone"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                className="rounded-lg"
                disabled={impersonated || savingTimezone || !(timezone || "").trim()}
                data-testid="profile-save-timezone"
              >
                {savingTimezone ? "Saving…" : "Save timezone"}
              </Button>
            </div>
          </form>
        </section>
      )}

      <section
        className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6 space-y-5"
        data-testid="profile-password-card"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent text-primary grid place-items-center">
            <LockKey className="w-5 h-5" weight="duotone" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Change password</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Use at least 8 characters. You will stay signed in after saving.
            </p>
          </div>
        </div>

        <form onSubmit={savePassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-current-password">Current password</Label>
            <Input
              id="profile-current-password"
              data-testid="profile-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={impersonated || savingPassword}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-new-password">New password</Label>
              <Input
                id="profile-new-password"
                data-testid="profile-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={impersonated || savingPassword}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-confirm-password">Confirm new password</Label>
              <Input
                id="profile-confirm-password"
                data-testid="profile-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={impersonated || savingPassword}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              className="rounded-lg"
              disabled={impersonated || savingPassword}
              data-testid="profile-save-password"
            >
              {savingPassword ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
